import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
const NavSubItem = lazy(() => import(/* webpackChunkName: "nav-subitem" */ './NavSubItem.jsx'));
import { motion, AnimatePresence } from "framer-motion";

export default function NavPopups({ path, openPath, onMouseEnter, onMouseLeave, isMobile }) {
    const [submenuHeight, setSubmenuHeight] = useState(0);
    const submenuRef = useRef(null);
    const debounceTimeoutRef = useRef(null);
    const isCalculatingRef = useRef(false);

    // Debounced height calculation to prevent rapid successive measurements
    const debouncedCalculateHeight = useCallback((forceImmediate = false) => {
        // Clear any pending calculation
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }

        // If already calculating and not forced, skip
        if (isCalculatingRef.current && !forceImmediate) {
            return;
        }

        // For empty paths, immediately set height to 0
        if (path.length === 0) {
            setSubmenuHeight(0);
            return;
        }

        const performCalculation = () => {
            if (!submenuRef.current || isCalculatingRef.current) return;

            isCalculatingRef.current = true;

            // Check if lazy components have finished loading
            const areLazyComponentsLoaded = () => {
                if (!submenuRef.current) return false;
                const fallbacks = submenuRef.current.querySelectorAll('[data-suspense-fallback]');
                return fallbacks.length === 0;
            };

            // Check if images are loaded
            const areImagesLoaded = () => {
                if (!submenuRef.current) return true;
                const images = submenuRef.current.querySelectorAll('img');
                return Array.from(images).every(img => img.complete);
            };

            // Wait for content to be fully ready
            const waitForContentReady = (callback, maxAttempts = 20, attempt = 0) => {
                if (attempt >= maxAttempts) {
                    callback();
                    return;
                }

                if (areLazyComponentsLoaded() && areImagesLoaded()) {
                    callback();
                } else {
                    const delay = Math.min(50 + (attempt * 25), 500);
                    setTimeout(() => waitForContentReady(callback, maxAttempts, attempt + 1), delay);
                }
            };

            const calculateHeight = () => {
                if (!submenuRef.current) return;

                // Force reflow to ensure DOM is updated
                submenuRef.current.offsetHeight;

                // Use multiple measurement methods for accuracy
                let height = submenuRef.current.offsetHeight;
                const scrollHeight = submenuRef.current.scrollHeight;
                height = Math.max(height, scrollHeight);
                const rect = submenuRef.current.getBoundingClientRect();
                height = Math.max(height, rect.height);

                // Ensure minimum height for valid content
                if (height < 50 && path.length > 0) {
                    // Retry after a short delay if height seems too small
                    setTimeout(() => {
                        if (submenuRef.current) {
                            const retryHeight = submenuRef.current.offsetHeight;
                            const retryScrollHeight = submenuRef.current.scrollHeight;
                            const finalHeight = Math.max(retryHeight, retryScrollHeight, rect.height);
                            setSubmenuHeight(finalHeight);
                        }
                        isCalculatingRef.current = false;
                    }, 100);
                    return;
                }

                setSubmenuHeight(height);
                isCalculatingRef.current = false;
            };

            waitForContentReady(() => {
                requestAnimationFrame(calculateHeight);
            });
        };

        if (forceImmediate) {
            performCalculation();
        } else {
            // Debounce with a short delay to prevent rapid successive calls
            debounceTimeoutRef.current = setTimeout(performCalculation, 50);
        }
    }, [path.length]);

    useEffect(() => {
        // Immediately set height to 0 for empty paths to prevent lingering small heights
        if (path.length === 0) {
            setSubmenuHeight(0);
            // Cancel any pending calculations
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }
            isCalculatingRef.current = false;
            return;
        }

        debouncedCalculateHeight(false);

        // Cleanup timeout on unmount
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            isCalculatingRef.current = false;
        };
    }, [path, debouncedCalculateHeight]);

    // Check if this is a federation menu path
    const isFederationMenu = path.length > 0 && path[0]?.federationMenu;

    // For federation menus, flatten all items and their children into a single list
    const flattenFederationMenu = (items, result = [], level = 0) => {
        items.forEach(item => {
            // Add the current item
            result.push({
                ...item,
                federationLevel: level, // Add level for styling
                originalItem: item // Keep reference to original
            });

            // Recursively add children if they exist
            if (item.children && item.children.length > 0) {
                flattenFederationMenu(item.children, result, level + 1);
            }
        });
        return result;
    };

    const federationContent = isFederationMenu ? (path[0]?.federationContent || null) : null;
    const currentMenu = isFederationMenu
        ? flattenFederationMenu(path[0].children || [])
        : (path[path.length - 1]?.children || []);

    const handleBack = () => {
        openPath(path.slice(0, -1));
    };

    const handleMouseEnter = () => {
        if (onMouseEnter) onMouseEnter();
        // Don't call openPath(path) here as it causes unwanted content changes
    };

    const handleMouseLeave = () => {
        if (onMouseLeave) onMouseLeave();
        // Don't call openPath([]) here as it's handled by the parent component
    };

    return (
        <motion.div
            className="popup-navigation"
            style={{ height: submenuHeight }}
            initial={{ height: 0 }}
            animate={{ height: submenuHeight }}
            exit={{ height: 0 }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <AnimatePresence>
                {path.length > 1 && (
                    <motion.div
                        key="back-button"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="buttons"
                    >
                        <button className="back-arrow btn btn-link" onClick={handleBack}>
                            <span className="ion ion-md-arrow-back mr-2" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
            <motion.div
                key="submenu"
                ref={submenuRef}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`submenu visible first-level ${isFederationMenu ? 'submenu-federation' : ''}`}
            >
                {isFederationMenu ? (
                    <div className={`federation-layout row ${isMobile ? 'mobile' : 'desktop'}`}>
                        <div className="federation-list col-12 col-lg-8">
                            <ul className={`items-lt-${currentMenu.length}`}>
                                {currentMenu.map((item) => (
                                    <Suspense key={item.id} fallback={<li data-suspense-fallback>Lade Navigationsunterelement…</li>}>
                                        <NavSubItem
                                            item={item}
                                            path={path}
                                            openPath={openPath}
                                        />
                                    </Suspense>
                                ))}
                            </ul>
                        </div>
                        {federationContent && (
                            <div className="federation-content col-12 col-lg-4">
                                {federationContent.link ? (
                                    <a href={federationContent.link} target="_blank" rel="noopener noreferrer" className="federation-content-link">
                                        <h4 className="federation-header">Aktuelles aus dem Bund FeG:</h4>
                                        {federationContent.image_url && (
                                            <img className="federation-image" src={federationContent.image_url} alt={federationContent.headline || ''} />
                                        )}
                                        {federationContent.headline && (
                                            <h3 className="federation-headline">{federationContent.headline}</h3>
                                        )}
                                        {federationContent.short && (
                                            <p className="federation-short">{federationContent.short}</p>
                                        )}
                                    </a>
                                ) : (
                                    <>
                                        <h4 className="federation-header">Aktuelles aus dem Bund FeG:</h4>
                                        {federationContent.headline && (
                                            <h3 className="federation-headline">{federationContent.headline}</h3>
                                        )}
                                        {federationContent.image_url && (
                                            <img className="federation-image" src={federationContent.image_url} alt={federationContent.headline || ''} />
                                        )}
                                        {federationContent.short && (
                                            <p className="federation-short">{federationContent.short}</p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <ul className={`items-lt-${currentMenu.length}`}>
                        {currentMenu.map((item) => (
                            <Suspense key={item.id} fallback={<li data-suspense-fallback>Lade Navigationsunterelement…</li>}>
                                <NavSubItem
                                    item={item}
                                    path={path}
                                    openPath={openPath}
                                />
                            </Suspense>
                        ))}
                    </ul>
                )}
            </motion.div>
        </motion.div>
    );
};